import React, { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useSalePage } from '../contexts/SalePageContext';
import { useAuthContext } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSubscription';
 

interface SalePageGuardProps {
  children: React.ReactNode;
  requireActive?: boolean;
  checkPageLimit?: boolean; // Check if user has reached page limit
}

const SalePageGuard: React.FC<SalePageGuardProps> = ({ children, requireActive = false, checkPageLimit = false }) => {
  const { user, loading: authLoading } = useAuthContext();
  const { subscription, loading: subLoading } = useSalePage();
  const { tier } = useSubscription();
  const [searchParams] = useSearchParams();
  const [timeoutReached, setTimeoutReached] = useState(false);
  
  // Check if editing existing page (has ?salePageId= parameter)
  const isEditMode = searchParams.has('salePageId');

  // Timeout protection - if loading takes more than 10 seconds, show error
  useEffect(() => {
    console.log('[SalePageGuard] Loading state:', { authLoading, subLoading });
    
    if (authLoading || subLoading) {
      const timer = setTimeout(() => {
        console.error('[SalePageGuard] Loading timeout reached');
        setTimeoutReached(true);
      }, 10000); // 10 seconds timeout
      
      return () => clearTimeout(timer);
    }
  }, [authLoading, subLoading]);

  // Show timeout error
  if (timeoutReached) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-8 bg-white rounded-xl shadow-lg">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">เกิดข้อผิดพลาด</h2>
          <p className="text-gray-600 mb-6">ไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่อีกครั้ง</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            โหลดหน้าใหม่
          </button>
        </div>
      </div>
    );
  }

  // Show minimal loading only for auth and subscription
  if (authLoading || subLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    console.log('[SalePageGuard] No user found, redirecting to login');
    // Force redirect using window.location to ensure it works
    window.location.href = '/login?redirect=/dashboard';
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังเปลี่ยนเส้นทาง...</p>
        </div>
      </div>
    );
  }

  // Pro+ Yearly users have automatic access
  const isProPlusYearly = tier === 'pro_plus_yearly';
  const hasPendingEmailVerification = subscription?.status === 'pending_email_verification';
  
  // Allow dashboard access even without subscription (they can see empty state and buy)
  // No redirect to pricing page - let users see dashboard first
  
  // For pages that require active subscription (like /sale-page builder, messages, payments)
  if (requireActive) {
    // Check if subscription is active or trial
    const hasValidSubscription = subscription?.status === 'active' || 
                                  subscription?.status === 'trial' ||
                                  isProPlusYearly;
    
    // Pending email verification users can access dashboard but not pages that require active
    if (hasPendingEmailVerification && !hasValidSubscription) {
      return <Navigate to="/dashboard" replace />;
    }

    if (!hasValidSubscription) {
      // Redirect to dashboard instead of pricing
      return <Navigate to="/dashboard" replace />;
    }
  }

  // Note: Page limit check moved to SalePage component itself to avoid blocking
  // This guard only checks subscription status, not page count

  return <>{children}</>;
};

export default SalePageGuard;
